/**
 * Sets a cookie in the document with the specified name, value, and optional expiration time.
 *
 * @param {string} name - The name of the cookie.
 * @param {string} value - The value to be stored in the cookie.
 * @param {number} [days] - Optional number of days until the cookie expires. If not provided, the cookie will be a session cookie.
 * @param {string} [sameSite] - Optional SameSite attribute value ('Lax', 'Strict', 'None'). Defaults to 'Lax'.
 * @param {boolean} [secure] - Optional flag to indicate if the cookie should be set as Secure. Recommended with SameSite=None.
 */
export function setCookie(name: string, value: string, days?: number, sameSite: string = 'Lax', secure: boolean = false): void {
  let expires = '';
  if (days) {
    const date = new Date();
    date.setTime(date.getTime() + (days * 24 * 60 * 60 * 1000));
    expires = `; expires=${date.toUTCString()}`;
  }

  let cookieString = `${name}=${encodeURIComponent(value || '')}${expires}; path=/; SameSite=${sameSite}`;

  if (secure) {
    cookieString += '; Secure';
  }

  document.cookie = cookieString;
}

/**
 * Retrieves the value of a cookie by its name.
 * 
 * @param {string} name - The name of the cookie to retrieve.
 * @returns {string | null} - The value of the cookie, or null if the cookie does not exist.
 */
export function getCookie(name: string): string | null {
  const cookies = document.cookie.split(';');
  for (let i = 0; i < cookies.length; i++) {
    const cookie = cookies[i].trim();
    const keyValue = cookie.split('=');
    if (keyValue.length < 2) {
      continue;
    }

    const key = keyValue[0];
    const value = keyValue[1];

    if (key === name) {
      return decodeURIComponent(value);
    }
  }
  return null;
}